import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    InputSignal,
    OnDestroy,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {onEach} from '../../../utils/observable-utils';
import {PaymentModel} from "../../payment.model";
import {
    GOOGLE_PAY_SCRIPT_URL,
    IntegratedPaymentProvider,
    PaymentAuthorisationType,
    WalletPaymentErrorType,
    WalletPaymentSubmissionFailure,
    WalletPaymentSubmissionResult
} from '../../payment';
import {Color} from "../../../ui-elements/color";
import {SpinnerComponent} from "../../../ui-elements/spinner/spinner.component";
import {TyroPayRequestError} from "../../tyro";
import {CssVarDirective} from '../../../utils/css-var.directive';
import {ConfigurationModel} from '../../../configuration/configuration.model';
import {ExternalScriptLoaderService} from '../../../external-api/external-script-loader.service';

const GOOGLE_PAY_BUTTON_BRIDGE_SOURCE: string = "woa-google-pay-button-bridge";
const GOOGLE_PAY_BUTTON_BRIDGE_RESPONSE_SOURCE: string = "woa-google-pay-button-bridge-response";

/**
 * Renders pay.js's button either inside a sandboxed, opaque-origin iframe or directly in the main page -
 * see isSandboxed() and createGooglePayButton() below for which and why. Either way, it's only ever used
 * to render a brand-compliant button here; the actual payment flow is handled separately by the active
 * payment provider's own SDK (Adyen/Stripe/Tyro), which never touches this PaymentsClient.
 *
 * The sandboxed bridge document lives as a static asset, assets/bridges/google-pay-bridge.html, which the
 * edge is configured to exempt from the app's PCI-mandated CSP.
 */

@Component({
    selector: "woa-google-pay-button",
    templateUrl: "./google-pay-button.component.html",
    styleUrl: "./google-pay-button.component.scss",
    host: {
        "[class.disabled]": "disabled()",
        "[class.woa-button]": "true"
    },
    imports: [
        CssVarDirective,
        SpinnerComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GooglePayButtonComponent implements OnDestroy, AfterViewInit {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private element: ElementRef = inject(ElementRef);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public paymentModel: PaymentModel = inject(PaymentModel);
    private externalScriptLoaderService: ExternalScriptLoaderService = inject(ExternalScriptLoaderService);

    IntegratedPaymentProvider = IntegratedPaymentProvider;

    public readonly type: InputSignal<"plain" | "checkout"> = input.required<"plain" | "checkout">();

    public readonly passive: InputSignal<boolean> = input<boolean>(false);
    public readonly disabled: InputSignal<boolean> = input<boolean>(false);
    public readonly showSpinner: InputSignal<boolean> = input<boolean>(false);

    public pay: OutputEmitterRef<void> = output<void>();
    public paymentStarted: OutputEmitterRef<void> = output<void>();
    public paymentComplete: OutputEmitterRef<WalletPaymentSubmissionResult> = output<WalletPaymentSubmissionResult>();
    public paymentFailed: OutputEmitterRef<WalletPaymentSubmissionFailure> = output<WalletPaymentSubmissionFailure>();
    public paymentCancelled: OutputEmitterRef<void> = output<void>();

    protected googlePayButtonContainer: Signal<ElementRef | undefined> = viewChild("googlePayButtonContainer");

    public readonly color: WritableSignal<"black" | "white"> = signal<"black" | "white">(undefined);
    private readonly payFormReady: WritableSignal<boolean> = signal<boolean>(false);
    private readonly payFormError: WritableSignal<string> = signal<string>(undefined);
    private readonly passiveButtonReady: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isSandboxed: WritableSignal<boolean> = signal<boolean>(!this.externalScriptLoaderService.isLoaded(GOOGLE_PAY_SCRIPT_URL));

    public readonly isPassive: Signal<boolean> = computed<boolean>(() =>
        this.passive() || this.paymentModel.paymentProvider() !== IntegratedPaymentProvider.TYRO);

    public readonly showTyroSpinner: Signal<boolean> = computed<boolean>(() =>
        !this.isPassive() && (!this.paymentModel.paymentPlatformPublicKey() || (!this.payFormReady() && !this.payFormError())));

    public readonly showLoadingSpinner: Signal<boolean> = computed<boolean>(() =>
        this.isPassive() && !this.passiveButtonReady());

    private iframe: HTMLIFrameElement;
    private iframeReady: boolean = false;
    private paymentsClient: any;
    private googleButton: HTMLElement;
    private tyro: any;
    private payForm: any;
    private tyroPayFormHolder: Element;
    private paymentPlatformPublicKey: string;
    private previousColor: "black" | "white";
    private previousType: "plain" | "checkout";
    private destroyed: boolean = false;

    private readonly handleBridgeMessage = (event: MessageEvent): void => {
        if (event.source !== this.iframe?.contentWindow || event.data?.source !== GOOGLE_PAY_BUTTON_BRIDGE_RESPONSE_SOURCE) {
            return;
        }
        if (event.data.event === "click") {
            if (!this.disabled()) {
                this.pay.emit();
            }
        } else if (event.data.event === "error") {
            console.error("Google Pay button bridge failed", event.data.message);
        } else if (event.data.event === "rendered") {
            this.passiveButtonReady.set(true);
        }
    };

    constructor() {
        effect(() => {
            if (this.configurationModel.app()) {
                this.createGooglePayButton();
            }
        })

        effect(() => this.enableOrDisableTyroPayForm());

        if (this.isSandboxed()) {
            // An ancestor's @if can detach and reattach this component's projected content without
            // destroying it (e.g. the page-footer hiding during order re-initiation), which silently
            // reloads the iframe - watch for the detachment itself so the spinner reappears immediately,
            // rather than waiting for that reload's own load event to eventually fire. Not a concern for
            // the non-sandboxed button below - a plain <div>'s children survive detach/reattach untouched.
            const bodyObserver: MutationObserver = new MutationObserver(() => {
                if (this.iframe && !this.iframe.isConnected) {
                    this.passiveButtonReady.set(false);
                }
            });
            bodyObserver.observe(document.body, {childList: true, subtree: true});
            this.destroyRef.onDestroy(() => bodyObserver.disconnect());

            this.destroyRef.onDestroy(() => window.removeEventListener("message", this.handleBridgeMessage));
        }
    }

    async ngAfterViewInit(): Promise<void> {
        onEach(this.paymentModel.paymentDetailsApplied$, async () => this.setupTyroPayForm(), this.destroyRef);
        return this.setupTyroPayForm();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
    }

    /**
     * Intermittent rendering/click-response issues with the sandboxed button, under time pressure to
     * stabilise, brought back the non-sandboxed path as a fallback - gated by isSandboxed(), which is true
     * unless pay.js is already loaded in the main page (see the static tag in index.html). Only the
     * sandboxed path needs the surrounding lifecycle machinery below (message listener, detach detection) -
     * the non-sandboxed one doesn't run in an iframe with a reloadable browsing context to compensate for.
     */
    private createGooglePayButton(): void {
        if (!this.isPassive() || !this.googlePayButtonContainer() || this.destroyed) {
            return;
        }
        if (this.isSandboxed()) {
            this.createSandboxedGooglePayButton();
        } else {
            void this.createDirectGooglePayButton();
        }
    }

    /**
     * Runs pay.js inside a sandboxed, opaque-origin iframe rather than the main page (see
     * assets/bridges/google-pay-bridge.html). Only the button's visual chrome and click event cross the boundary.
     */
    private createSandboxedGooglePayButton(): void {
        // Runs regardless of iframe readiness below, so the button's background color shows immediately -
        // only the bridge communication itself waits for the iframe to be ready.
        this.determineColor();
        if (!this.iframeReady || (this.previousColor === this.color() && this.previousType === this.type())) {
            return;
        }

        this.iframe = this.googlePayButtonContainer().nativeElement;
        if (!this.iframe.contentWindow) {
            // Detached mid-transition (the page-footer's @if removed it) - the reload this causes will
            // fire onIframeLoad() again, which resets previousColor/Type and retries.
            return;
        }
        this.previousType = this.type();
        this.previousColor = this.color();
        window.addEventListener("message", this.handleBridgeMessage);

        this.iframe.contentWindow.postMessage({
            source: GOOGLE_PAY_BUTTON_BRIDGE_SOURCE,
            type: "configure",
            buttonType: this.type(),
            buttonColor: this.color()
        }, "*");
    }

    /**
     * Renders pay.js's button directly into the main page - the pre-sandboxing behaviour, reinstated as a
     * fallback. onClick fires straight into this.pay.emit(), with no bridge/postMessage roundtrip needed.
     */
    private async createDirectGooglePayButton(): Promise<void> {
        if (!this.paymentsClient) {
            try {
                await this.externalScriptLoaderService.loadScript(GOOGLE_PAY_SCRIPT_URL);
            } catch (error) {
                console.error("Failed to load Google Pay script", error);
                return;
            }
            if (!this.isPassive() || !this.googlePayButtonContainer() || this.destroyed) {
                return;
            }
            // noinspection JSUnresolvedReference
            this.paymentsClient = new (<any>window).google.payments.api.PaymentsClient({environment: "TEST"});
        }

        this.determineColor();
        if (this.previousColor === this.color() && this.previousType === this.type()) {
            return;
        }
        this.previousType = this.type();
        this.previousColor = this.color();

        if (this.googleButton) {
            this.googlePayButtonContainer()?.nativeElement.removeChild(this.googleButton);
        }
        this.googleButton = this.paymentsClient.createButton({
            onClick: (event: MouseEvent) => {
                event.stopPropagation();
                if (!this.disabled()) {
                    this.pay.emit();
                }
            },
            buttonColor: this.color(),
            buttonType: this.type(),
            buttonSizeMode: "fill"
        });
        this.googlePayButtonContainer()?.nativeElement.appendChild(this.googleButton);

        // Unlike the sandboxed bridge, there's no postMessage "rendered" event to wait for here - the
        // button exists in the main page's own DOM as soon as createButton()/appendChild() return.
        this.passiveButtonReady.set(true);
    }

    /**
     * Removing an iframe from the DOM and reinserting it (e.g. when the page-footer's @if toggles, which
     * doesn't destroy this component since it just detaches/reattaches the projected content) discards its
     * browsing context and reloads it from scratch - even though the element is the same JS object, its
     * bridge state (pay.js, the message listener) is wiped. So any load, not just the first, means the
     * bridge is a blank slate and must be freshly configured - clearing previousColor/Type forces that.
     */
    protected onIframeLoad(): void {
        this.iframeReady = true;
        this.passiveButtonReady.set(false);
        this.previousColor = undefined;
        this.previousType = undefined;
        this.createGooglePayButton();
    }

    private async setupTyroPayForm(): Promise<void> {
        if (this.isPassive() || this.paymentModel.paymentProvider() !== IntegratedPaymentProvider.TYRO || this.destroyed) {
            return;
        }

        this.enableOrDisableTyroPayForm();

        // Tyro uses the following test to check that it can inject its form - we can't use a ViewChild, because it needs to be in the HTML DOM.
        this.tyroPayFormHolder = document.querySelector("#tyro-google-pay-form-holder");
        if (!this.tyroPayFormHolder) {
            setTimeout(async () => await this.setupTyroPayForm(), 500);
            return;
        }

        // The tyro secret (paymentPlatformPublicKey) changes if the order is re-initiated - the tyro form needs to be rebuilt.
        this.determineColor();
        if (!this.paymentModel.paymentPlatformPublicKey() || this.paymentPlatformPublicKey === this.paymentModel.paymentPlatformPublicKey() || (this.previousColor === this.color() && this.previousType === this.type())) {
            return;
        }
        this.paymentPlatformPublicKey = this.paymentModel.paymentPlatformPublicKey();
        this.previousType = this.type();
        this.previousColor = this.color();

        try {
            console.log("Initialising Tyro for google pay button. Live Mode = ", this.paymentModel.paymentPlatformLiveMode());
            this.tyro = (<any>window).Tyro({
                liveMode: this.paymentModel.paymentPlatformLiveMode()
            });
            await this.tyro.init(this.paymentPlatformPublicKey);

            this.payForm = this.tyro.createPayForm({
                options: {
                    googlePay: {
                        enabled: true,
                        merchantInfo: {
                            merchantName: this.configurationModel.app()?.googlePayMerchantName,
                            merchantId: this.paymentModel.paymentPlatformLiveMode() ? this.configurationModel.app()?.googlePayMerchantId : undefined
                        }
                    },
                    creditCardForm: {
                        enabled: false
                    },
                    walletButtons: {
                        singleButtonOnly: true
                    }
                },
                styleProps: {
                    googlePayButton: {
                        buttonColor: this.color(),
                        buttonType: this.type()
                    },
                    bodyMinWidth: 290,
                    bodyMaxWidth: 980,
                    labelPosition: "block",
                    walletPaymentsButtonsMargin: "0",
                    walletPaymentsButtonsHeight: "100%",
                    walletPaymentsButtonsWidth: "100%"
                }
            });
            if (!this.payForm.isGooglePaySupported()) {
                console.log("Tyro pay form does not support google pay");
                this.payFormError.set("Tyro pay form does not support google pay");
                return;
            }
            if ((<HTMLElement>this.tyroPayFormHolder).firstChild) {
                this.tyroPayFormHolder.removeChild((<HTMLElement>this.tyroPayFormHolder).firstChild);
            }
            await this.payForm.inject("#tyro-google-pay-form-holder");
            this.payForm.setReadyListener(() => {
                this.enableOrDisableTyroPayForm();
                this.payFormReady.set(true);
            });

            // noinspection JSUnusedLocalSymbols
            this.payForm.setWalletPaymentBeginListener((paymentType: string) => {
                if (this.disabled()) {
                    throw "Google pay button disabled";
                }
                this.paymentStarted.emit();
            });

            // noinspection JSUnusedLocalSymbols,DuplicatedCode
            this.payForm.setWalletPaymentCompleteListener(async (paymentType: string, error: TyroPayRequestError) => {
                if (error) {
                    this.paymentFailed.emit(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.type, error.errorMessage, error.errorCode, error.gatewayCode));
                } else {
                    this.paymentComplete.emit(new WalletPaymentSubmissionResult(PaymentAuthorisationType.TYRO_PAYMENT_INTENT));
                }
            });

            // noinspection JSUnusedLocalSymbols
            this.payForm.setWalletPaymentCancelledListener((paymentType: string) => this.paymentCancelled.emit());
        } catch (error) {
            console.log("Failed to create Tyro pay form for Google Pay button", error);
            this.payFormError.set(error.message.replace(/\.$/, ""));
        }
    }

    private determineColor(): void {
        const bg: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--theme-color-pair-background").trim();
        if (bg) {
            const rgb: string[] = bg.split(" ");
            const bgColor: Color = Color.fromRGB(parseInt(rgb[0]), parseInt(rgb[1]), parseInt(rgb[2]));
            this.color.set(bgColor.getContrastRatio(Color.BLACK) > bgColor.getContrastRatio(Color.WHITE) ? "black" : "white");
        }
    }

    private enableOrDisableTyroPayForm(): void {
        if (this.tyroPayFormHolder && (<HTMLElement>this.tyroPayFormHolder).firstChild) {
            (<HTMLElement>(<HTMLElement>this.tyroPayFormHolder).firstChild).tabIndex = this.disabled() ? -1 : undefined;
        }
    }
}
